/**
 * Canvas overlays drawn into uPlot's draw/drawClear hooks:
 * leg bands, changeover bands (per-engine lanes), the scatter y=x diagonal, and
 * filter threshold lines. Plus the changeover legend-strip data projection.
 *
 * All draws use device pixels (uPlot's bbox is device px) and read theme colors
 * from CSS vars where the blob did. Time parsing is UTC via `format.ts`.
 */

import type uPlot from 'uplot';
import { runtime } from '../plot-runtime.ts';
import { plotState } from '../../../state/plot-state.ts';
import { changeoverLegend, type ChangeoverLegend } from '../plot-ui-state.ts';
import { getCssVar } from './chart-dom.ts';
import { parseLegSec } from './format.ts';
import type { Threshold } from './uplot-helpers.ts';
import type { Changeover } from '../../../api/types.ts';
import type { OverlayShape } from '../../../state/chart-overlays.ts';

const dpr = (): number => window.devicePixelRatio || 1;

/* ── Leg bands ──────────────────────────────────────────── */

export function drawLegBands(u: uPlot, legs: Array<{ leg_key?: string; start: string | null; end: string | null }>): void {
  if (!legs || !legs.length) return;
  const ctx = u.ctx;
  const { top, height } = u.bbox;
  const tints = ['rgba(99,132,255,0.10)', 'rgba(81,207,102,0.10)'];
  const borders = ['rgba(99,132,255,0.38)', 'rgba(81,207,102,0.38)'];
  ctx.save();
  const fontPx = Math.round(13 * dpr());
  ctx.font = `700 ${fontPx}px system-ui, -apple-system, sans-serif`;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';

  const bands: Array<{ lx: number; rx: number; label: string }> = [];
  for (let i = 0; i < legs.length; i++) {
    const leg = legs[i]!;
    const startSec = parseLegSec(leg.start);
    const endSec = parseLegSec(leg.end);
    if (!Number.isFinite(startSec) || !Number.isFinite(endSec)) continue;
    const x0 = u.valToPos(startSec, 'x', true);
    const x1 = u.valToPos(endSec, 'x', true);
    if (x1 < u.bbox.left || x0 > u.bbox.left + u.bbox.width) continue;
    const lx = Math.max(u.bbox.left, Math.min(x0, x1));
    const rx = Math.min(u.bbox.left + u.bbox.width, Math.max(x0, x1));
    if (rx - lx <= 0.5) continue;
    ctx.fillStyle = tints[i % 2]!;
    ctx.fillRect(lx, top, rx - lx, height);
    ctx.strokeStyle = borders[i % 2]!;
    ctx.lineWidth = 1;
    ctx.strokeRect(lx + 0.5, top + 0.5, rx - lx - 1, height - 1);
    bands.push({ lx, rx, label: String(leg.leg_key || '') });
  }

  // Labels: anchor at each band start, pick the topmost free row (6 max).
  const gapX = 8 * dpr();
  const rowGap = 2 * dpr();
  const rowH = fontPx + rowGap;
  const MAX_ROWS = 6;
  const rowRight = new Array(MAX_ROWS).fill(-Infinity);
  const textColor = getCssVar('--text') || '#e8ebf4';
  const inset = Math.max(2, Math.round(2 * dpr()));
  for (const b of bands) {
    if (!b.label) continue;
    const textW = ctx.measureText(b.label).width;
    const anchorX = Math.round(b.lx) + inset;
    let row = -1;
    for (let r = 0; r < MAX_ROWS; r++) {
      if (anchorX >= rowRight[r]) {
        row = r;
        break;
      }
    }
    if (row < 0) {
      row = 0;
      for (let r = 1; r < MAX_ROWS; r++) if (rowRight[r] < rowRight[row]) row = r;
    }
    const textY = Math.round(top + inset + row * rowH);
    ctx.fillStyle = textColor;
    ctx.fillText(b.label, anchorX, textY);
    rowRight[row] = anchorX + textW + gapX;
  }
  ctx.restore();
}

/* ── Changeover bands ───────────────────────────────────── */

export const CHANGEOVER_COLORS: Record<string, { fill: string; stroke: string; solid: string }> = {
  flush: { fill: 'rgba(244,67,54,0.55)', stroke: 'rgba(244,67,54,0.95)', solid: '#e74c3c' },
  standard: { fill: 'rgba(156,89,217,0.55)', stroke: 'rgba(156,89,217,0.95)', solid: '#9c59d9' },
  unknown: { fill: 'rgba(140,140,140,0.55)', stroke: 'rgba(140,140,140,0.95)', solid: '#8a9099' },
};

export function changeoverTypeKey(type: string | null | undefined): string {
  if (!type) return 'unknown';
  const s = String(type).toLowerCase();
  if (s.includes('flush')) return 'flush';
  if (s.includes('standard')) return 'standard';
  return 'unknown';
}

export function changeoverColor(type: string | null | undefined): { fill: string; stroke: string; solid: string } {
  return CHANGEOVER_COLORS[changeoverTypeKey(type)]!;
}

export function changeoverTypeDisplay(type: string | null | undefined): string {
  if (!type) return 'Unknown type';
  const s = String(type).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return 'Unknown type';
  return s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

export function formatChangeoverDuration(startSec: number, endSec: number): string {
  if (!Number.isFinite(startSec) || !Number.isFinite(endSec)) return '';
  const total = Math.max(0, Math.round(endSec - startSec));
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}

function changeoverLabel(co: Changeover): string {
  const parts: string[] = [co.engine || ''];
  if (co.source_fuel && co.target_fuel) parts.push(`${co.source_fuel}→${co.target_fuel}`);
  else if (co.source_fuel) parts.push(`from ${co.source_fuel}`);
  else if (co.target_fuel) parts.push(`to ${co.target_fuel}`);
  const dur = formatChangeoverDuration(parseLegSec(co.start), parseLegSec(co.end));
  if (dur) parts.push(dur);
  return parts.filter(Boolean).join(' · ');
}

/** Sort DG1, DG2, … by numeric suffix so lanes stay stable across renders. */
export function compareEngines(a: string, b: string): number {
  const na = parseInt(String(a).replace(/\D/g, ''), 10);
  const nb = parseInt(String(b).replace(/\D/g, ''), 10);
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
  return a < b ? -1 : a > b ? 1 : 0;
}

export function drawChangeoverBands(u: uPlot, changeovers: Changeover[]): void {
  if (!changeovers || !changeovers.length) return;
  const engines = Array.from(new Set(changeovers.map((c) => c.engine || 'DG?'))).sort(compareEngines);
  const lane = new Map(engines.map((e, i) => [e, i]));

  const ctx = u.ctx;
  const { top, left, width, height } = u.bbox;
  const LANE_H = Math.max(14, Math.round(16 * dpr()));
  const laneGap = Math.max(1, Math.round(1 * dpr()));
  const totalH = engines.length * LANE_H + Math.max(0, engines.length - 1) * laneGap;
  const maxBand = Math.max(LANE_H, Math.floor(height / 2));
  let effLaneH = LANE_H;
  let effGap = laneGap;
  if (totalH > maxBand && engines.length > 0) {
    const perLane = Math.max(10, Math.floor(maxBand / engines.length));
    effLaneH = perLane - laneGap;
    if (effLaneH < 8) {
      effLaneH = 8;
      effGap = 0;
    }
  }
  const fontPx = Math.max(10, Math.min(effLaneH - 4, Math.round(11 * dpr())));

  ctx.save();
  ctx.font = `600 ${fontPx}px system-ui, -apple-system, sans-serif`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';

  const plotRight = left + width;
  for (const co of changeovers) {
    const startSec = parseLegSec(co.start);
    const endSec = parseLegSec(co.end);
    if (!Number.isFinite(startSec) || !Number.isFinite(endSec)) continue;
    const x0 = u.valToPos(startSec, 'x', true);
    const x1 = u.valToPos(endSec, 'x', true);
    if (x1 < left || x0 > plotRight) continue;
    const lx = Math.max(left, Math.min(x0, x1));
    const rx = Math.min(plotRight, Math.max(x0, x1));
    const bandW = rx - lx;
    if (bandW < 0.75) continue;

    const laneIdx = lane.get(co.engine) ?? 0;
    const ly = top + laneIdx * (effLaneH + effGap);
    const color = changeoverColor(co.type);

    ctx.fillStyle = color.fill;
    ctx.fillRect(lx, ly, bandW, effLaneH);
    ctx.strokeStyle = color.stroke;
    ctx.lineWidth = 1;
    ctx.strokeRect(lx + 0.5, ly + 0.5, Math.max(1, bandW - 1), effLaneH - 1);

    const label = changeoverLabel(co);
    if (!label) continue;
    const textW = ctx.measureText(label).width;
    const padX = Math.max(4, Math.round(4 * dpr()));
    const textY = ly + effLaneH / 2;
    let textX: number;
    let inside = false;
    if (bandW >= textW + padX * 2) {
      textX = lx + padX;
      inside = true;
    } else {
      textX = rx + padX;
      if (textX + textW > plotRight) textX = Math.max(left + padX, plotRight - textW - padX);
    }
    ctx.save();
    ctx.beginPath();
    ctx.rect(left, ly - 1, width, effLaneH + 2);
    ctx.clip();
    ctx.fillStyle = inside ? '#ffffff' : getCssVar('--text') || '#e8ebf4';
    ctx.fillText(label, textX, textY);
    ctx.restore();
  }
  ctx.restore();
}

/**
 * Project the changeover legend-strip state (blob `renderChangeoverLegendStrip`,
 * minus DOM) into the `changeoverLegend` atom - `<plot-chart>` renders it.
 */
export function refreshChangeoverLegend(): void {
  const s = plotState.value;
  const data = runtime.lastData;
  const visible =
    s.show_changeovers &&
    s.plot_type === 'line' &&
    !!data &&
    data.plot_type !== 'histogram' &&
    data.plot_type !== 'heatmap' &&
    data.plot_type !== 'correlation' &&
    data.plot_type !== 'pie' &&
    data.plot_type !== 'box' &&
    data.plot_type !== 'number' &&
    data.plot_type !== 'gauge' &&
    data.plot_type !== 'sankey' &&
    data.plot_type !== 'table' &&
    data.plot_type !== 'map' &&
    data.x_is_time &&
    runtime.lastChangeovers != null;
  if (!visible) {
    changeoverLegend.set(null);
    return;
  }
  const list = runtime.lastChangeovers || [];
  if (!list.length) {
    changeoverLegend.set({ groups: [], engines: [], empty: true });
    return;
  }
  const typeKeys = new Map<string, string | null>();
  const engineSet = new Set<string>();
  for (const co of list) {
    typeKeys.set(changeoverTypeKey(co.type), co.type);
    if (co.engine) engineSet.add(co.engine);
  }
  const order = ['flush', 'standard', 'unknown'];
  const groups = order
    .filter((k) => typeKeys.has(k))
    .map((k) => ({
      key: k,
      display:
        k === 'unknown' && !typeKeys.get(k)
          ? 'Unknown type'
          : changeoverTypeDisplay(typeKeys.get(k)),
      solid: (CHANGEOVER_COLORS[k] ?? CHANGEOVER_COLORS.unknown!).solid,
    }));
  const engines = Array.from(engineSet).sort(compareEngines);
  changeoverLegend.set({ groups, engines, empty: false } satisfies ChangeoverLegend);
}

/* ── Diagonal (scatter y=x) ─────────────────────────────── */

export function drawDiagonal(u: uPlot): void {
  const xs = u.scales.x;
  const ys = u.scales.y;
  if (!xs || !ys) return;
  if (xs.min == null || xs.max == null || ys.min == null || ys.max == null) return;
  const a = Math.max(xs.min, ys.min);
  const b = Math.min(xs.max, ys.max);
  if (!(a < b)) return;
  const px1 = u.valToPos(a, 'x', true);
  const py1 = u.valToPos(a, 'y', true);
  const px2 = u.valToPos(b, 'x', true);
  const py2 = u.valToPos(b, 'y', true);
  const ctx = u.ctx;
  ctx.save();
  ctx.strokeStyle = getCssVar('--text-dim') || 'rgba(200,200,200,0.6)';
  ctx.lineWidth = 1 * dpr();
  ctx.setLineDash([5 * dpr(), 4 * dpr()]);
  ctx.beginPath();
  ctx.moveTo(px1, py1);
  ctx.lineTo(px2, py2);
  ctx.stroke();
  ctx.restore();
}

/* ── Rounded-rect path helper ───────────────────────────── */

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/* ── On-canvas color legend (scatter discrete color-by) ─── */

/**
 * Draw a compact value→color legend panel inset in the top-right of the plot
 * area. On the canvas (not DOM) so it travels into PNG/JPG/PDF exports. Used for
 * discrete (categorical) color-by; the continuous gradient stays a DOM strip.
 */
export function drawColorLegendBox(
  u: uPlot,
  legend: { title: string; items: Array<{ label: string; color: string }> },
): void {
  const items = legend.items;
  if (!items.length) return;
  const ctx = u.ctx;
  const { left, top, width } = u.bbox;
  const d = dpr();

  const pad = Math.round(8 * d);
  const rowGap = Math.round(5 * d);
  const sw = Math.round(11 * d);
  const swGap = Math.round(7 * d);
  const titleFontPx = Math.round(11 * d);
  const itemFontPx = Math.round(11 * d);
  const titleGap = Math.round(6 * d);
  const radius = Math.round(6 * d);
  const inset = Math.round(8 * d);

  ctx.save();
  // Measure the widest row (title or swatch + label) for the panel width.
  ctx.font = `700 ${titleFontPx}px system-ui, -apple-system, sans-serif`;
  let maxText = legend.title ? ctx.measureText(legend.title).width : 0;
  ctx.font = `500 ${itemFontPx}px system-ui, -apple-system, sans-serif`;
  for (const it of items) {
    const w = sw + swGap + ctx.measureText(it.label).width;
    if (w > maxText) maxText = w;
  }
  const titleH = legend.title ? titleFontPx + titleGap : 0;
  const rowH = Math.max(sw, itemFontPx);
  const boxW = pad * 2 + maxText;
  const boxH = pad * 2 + titleH + items.length * rowH + (items.length - 1) * rowGap;
  const x = left + width - boxW - inset;
  const y = top + inset;

  const surface = getCssVar('--surface') || '#1a1d23';
  const border = getCssVar('--border') || '#2a2d36';
  const text = getCssVar('--text') || '#e8ebf4';
  const textDim = getCssVar('--text-dim') || '#8a9099';

  ctx.globalAlpha = 0.92;
  ctx.fillStyle = surface;
  roundRectPath(ctx, x, y, boxW, boxH, radius);
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.lineWidth = Math.max(1, Math.round(d));
  ctx.strokeStyle = border;
  roundRectPath(ctx, x, y, boxW, boxH, radius);
  ctx.stroke();

  let cy = y + pad;
  ctx.textAlign = 'left';
  if (legend.title) {
    ctx.font = `700 ${titleFontPx}px system-ui, -apple-system, sans-serif`;
    ctx.textBaseline = 'top';
    ctx.fillStyle = textDim;
    ctx.fillText(legend.title, x + pad, cy);
    cy += titleH;
  }
  ctx.font = `500 ${itemFontPx}px system-ui, -apple-system, sans-serif`;
  for (const it of items) {
    const swatchY = cy + (rowH - sw) / 2;
    ctx.fillStyle = it.color;
    roundRectPath(ctx, x + pad, swatchY, sw, sw, Math.round(2 * d));
    ctx.fill();
    ctx.lineWidth = Math.max(1, Math.round(d));
    ctx.strokeStyle = border;
    roundRectPath(ctx, x + pad, swatchY, sw, sw, Math.round(2 * d));
    ctx.stroke();
    ctx.fillStyle = text;
    ctx.textBaseline = 'middle';
    ctx.fillText(it.label, x + pad + sw + swGap, cy + rowH / 2);
    cy += rowH + rowGap;
  }
  ctx.restore();
}

/* ── Branding (bottom-right watermark) ──────────────────── */

/** The favicon wave motif, scaled into a `size`×`size` box at (x, y). */
function drawBrandMark(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
): void {
  const s = size / 32;
  const px = (n: number): number => x + n * s;
  const py = (n: number): number => y + n * s;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 2.4 * s;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(px(5), py(21));
  ctx.bezierCurveTo(px(9), py(21), px(9.5), py(11), px(14), py(11));
  ctx.bezierCurveTo(px(18.5), py(11), px(18), py(21), px(23), py(21));
  ctx.bezierCurveTo(px(25.5), py(21), px(26), py(15), px(27), py(15));
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(px(14), py(11), 2.4 * s, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * Faint product branding in the bottom-right of the plot area - mark + name.
 * Drawn on the canvas so it appears live and in every image/PDF export.
 */
export function drawBranding(u: uPlot): void {
  const ctx = u.ctx;
  const { left, top, width, height } = u.bbox;
  if (width < 120 || height < 80) return; // too cramped to bother
  const d = dpr();
  const text = 'EOS Data Explorer';
  const fontPx = Math.round(15 * d);
  const markSize = Math.round(20 * d);
  const gap = Math.round(8 * d);
  const inset = Math.round(10 * d);
  const color = getCssVar('--text') || '#e8ebf4';

  ctx.save();
  ctx.globalAlpha = 0.3;
  ctx.font = `600 ${fontPx}px system-ui, -apple-system, sans-serif`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'right';
  const right = left + width - inset;
  const bottom = top + height - inset;
  const cyCenter = bottom - markSize / 2;
  ctx.fillStyle = color;
  ctx.fillText(text, right, cyCenter);
  const textW = ctx.measureText(text).width;
  drawBrandMark(ctx, right - textW - gap - markSize, bottom - markSize, markSize, color);
  ctx.restore();
}

/* ── Filter thresholds ──────────────────────────────────── */

export function drawThresholds(u: uPlot, thresholds: Threshold[]): void {
  if (!thresholds.length) return;
  const ctx = u.ctx;
  const { left, top, width, height } = u.bbox;
  ctx.save();
  ctx.strokeStyle = '#ff6b6b';
  ctx.fillStyle = '#ff6b6b';
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 4]);
  ctx.font = '500 11px system-ui, -apple-system, sans-serif';
  ctx.textBaseline = 'bottom';
  for (const t of thresholds) {
    if (!(t.scale in u.scales)) continue;
    const y = u.valToPos(t.value, t.scale, true);
    if (y < top || y > top + height) continue;
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(left + width, y);
    ctx.stroke();
    ctx.fillText(t.label, left + 6, y - 2);
  }
  ctx.restore();
}

/* ── Registered overlays ────────────────────────────────── */

/**
 * Shapes handed in by a feature that does not own this chart - see
 * `state/chart-overlays.ts`.
 *
 * Drawn after the data and the app's own overlays, so a guest never hides the
 * thing somebody came to look at, and drawn dashed and half-transparent for the
 * same reason. A colour may be a `var(--token)`, which is resolved here because
 * a canvas cannot.
 */
export function drawRegisteredOverlays(u: uPlot, shapes: OverlayShape[]): void {
  if (!shapes.length) return;
  const ctx = u.ctx;
  const { left, top, width, height } = u.bbox;
  const d = dpr();
  const fallback = getCssVar('--text-dim') || '#888d97';

  const resolve = (color: string | undefined): string => {
    if (!color) return fallback;
    const match = /^var\((--[\w-]+)\)$/.exec(color.trim());
    if (!match) return color;
    return getCssVar(match[1]!) || fallback;
  };

  ctx.save();
  const fontPx = Math.round(11 * d);
  ctx.font = `500 ${fontPx}px system-ui, -apple-system, sans-serif`;
  ctx.lineWidth = 1;

  for (const shape of shapes) {
    const color = resolve(shape.color);

    if (shape.kind === 'line') {
      // A scale the chart has not got is not an error - a rule can name a
      // series that is simply not on this chart's right-hand axis today.
      if (!(shape.scale in u.scales)) continue;
      const y = u.valToPos(shape.value, shape.scale, true);
      if (!Number.isFinite(y) || y < top || y > top + height) continue;
      ctx.save();
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.setLineDash(shape.style === 'solid' ? [] : [5 * d, 4 * d]);
      ctx.beginPath();
      ctx.moveTo(left, y);
      ctx.lineTo(left + width, y);
      ctx.stroke();
      ctx.setLineDash([]);
      // Left, where `drawThresholds` already puts its labels - and away from
      // the branding in the bottom right, which a right-aligned label sits on
      // top of at the foot of the plot.
      ctx.textBaseline = 'bottom';
      ctx.textAlign = 'left';
      ctx.fillText(shape.label, left + 6 * d, y - 3 * d);
      ctx.restore();
      continue;
    }

    if (shape.kind === 'band') {
      const x0 = u.valToPos(shape.from, 'x', true);
      const x1 = u.valToPos(shape.to, 'x', true);
      if (!Number.isFinite(x0) || !Number.isFinite(x1)) continue;
      const lx = Math.max(left, Math.min(x0, x1));
      const rx = Math.min(left + width, Math.max(x0, x1));
      if (rx - lx <= 0.5) continue;
      ctx.save();
      ctx.globalAlpha = 0.16;
      ctx.fillStyle = color;
      ctx.fillRect(lx, top, rx - lx, height);
      ctx.globalAlpha = 1;
      ctx.strokeStyle = color;
      ctx.beginPath();
      ctx.moveTo(lx + 0.5, top);
      ctx.lineTo(lx + 0.5, top + height);
      ctx.stroke();
      if (shape.label && rx - lx > 40 * d) {
        ctx.fillStyle = color;
        ctx.textBaseline = 'top';
        ctx.textAlign = 'left';
        ctx.fillText(shape.label, lx + 4 * d, top + 3 * d);
      }
      ctx.restore();
      continue;
    }

    // mark
    const x = u.valToPos(shape.x, 'x', true);
    if (!Number.isFinite(x) || x < left || x > left + width) continue;
    const scale = shape.scale && shape.scale in u.scales ? shape.scale : null;
    const y =
      shape.y !== undefined && scale ? u.valToPos(shape.y, scale, true) : top + 8 * d;
    if (!Number.isFinite(y)) continue;
    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, 3.5 * d, 0, Math.PI * 2);
    ctx.fill();
    if (shape.label) {
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      ctx.fillText(shape.label, x + 6 * d, y);
    }
    ctx.restore();
  }

  ctx.restore();
}
